import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiRequest } from "../config/api";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  const token = searchParams.get("token");
  const orgId = searchParams.get("orgId");

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError("Invalid or missing reset token");
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("Both fields are required");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };
      
      // Add organization ID header if available
      if (orgId) {
        headers["X-Organization-Id"] = orgId;
      }

      const response = await apiRequest("/api/player/reset-password", {
        method: "POST",
        headers,
        body: JSON.stringify({
          token,
          newPassword,
        }),
      });

      if (response.ok) {
        setSuccess(true);
        // Redirect to player login after 3 seconds
        setTimeout(() => {
          navigate("/player/login");
        }, 3000);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to reset password");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppBar position="static" sx={{ mb: 4 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Reset Password
          </Typography>
        </Toolbar>
      </AppBar>

      <Container component="main" maxWidth="xs">
        <Paper elevation={6} sx={{ p: 4, mt: 8 }}>
          {success ? (
            <Box textAlign="center">
              <CheckCircleIcon sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Password Reset Successful!
              </Typography>
              <Typography color="text.secondary">
                Your password has been reset. Redirecting to login...
              </Typography>
            </Box>
          ) : !tokenValid ? (
            <Box textAlign="center">
              <Typography variant="h5" gutterBottom color="error">
                Invalid Reset Link
              </Typography>
              <Typography color="text.secondary" paragraph>
                This password reset link is invalid or has expired.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/player/login")}
              >
                Back to Login
              </Button>
            </Box>
          ) : (
            <>
              <Typography component="h1" variant="h5" align="center" gutterBottom>
                🔑 Create New Password
              </Typography>
              <Typography
                color="text.secondary"
                align="center"
                sx={{ mb: 3 }}
              >
                Enter your new password below
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="newPassword"
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1, mb: 2 }}
                >
                  Password must be at least 8 characters long
                </Typography>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Resetting Password...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>

                <Box textAlign="center" mt={2}>
                  <Button
                    onClick={() => navigate("/player/login")}
                    disabled={isLoading}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </>
  );
}

export default ResetPassword;
